import successionEndpoints from "./endpoints/succession-endpoints";
import apiConfig from "../config/api";
import http from "./http";

const {
  files,
  file,
  fileAudit,
  fileAction,
  fileDocuments,
  fileDocument,
  documentTemplate,
  documentTemplateVersion,
  register,
  generalRegistryEntry,
  registries,
  notaryChambers,
} = successionEndpoints;
const { baseURL } = apiConfig;

const url = (path) => `${baseURL}${path}`;
const dataOf = (response) => response.data;

// Numele fisierului nu se poate citi din antetul Content-Disposition in browser: API-ul nu il expune
// prin CORS pe raspunsurile cross-origin. Antetul ramane doar o rezerva pentru medii unde e disponibil
// (de exemplu testele Node); numele "adevarat" vine din datele apelantului (lista, documentul generat
// sau versiunea de sablon in vigoare).
const filenameFromContentDisposition = (headers) => {
  const value =
    headers &&
    (headers["content-disposition"] || headers["Content-Disposition"]);
  if (!value) return null;
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(value);
  return match ? decodeURIComponent(match[1]) : null;
};

const DEFAULT_DOCUMENT_NAME = "document.docx";
const DEFAULT_CDPS_NAME = "CDPS.docx";

// Cere raspunsul ca blob (necesar pentru fisiere binare cu axios) si alege numele: parametrul primit,
// apoi antetul (rezerva), apoi un nume implicit rezonabil.
const downloadBlob = (path, fileName, defaultName) =>
  http.get(url(path), { responseType: "blob" }).then((response) => ({
    blob: response.data,
    name:
      fileName ||
      filenameFromContentDisposition(response.headers) ||
      defaultName,
  }));

// Tablourile devin chei repetate (status=A&status=B), cum le leaga ASP.NET Core; valorile goale se omit.
export const buildQuery = (params) => {
  const parts = [];
  Object.keys(params).forEach((key) => {
    const value = params[key];
    const values = Array.isArray(value) ? value : [value];
    values
      .filter((item) => item !== null && item !== undefined && item !== "")
      .forEach((item) =>
        parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(item)}`)
      );
  });
  return parts.length ? `?${parts.join("&")}` : "";
};

// Spre deosebire de serviciile vechi, erorile nu sunt inghitite: promisiunea se respinge cu eroarea axios,
// iar pagina o traduce cu describeError (helpers/resdpsErrors). Tokenul il pune interceptorul din http.js.
const SuccessionService = {
  listFiles({
    statuses = [],
    year = null,
    search = "",
    page = 1,
    pageSize = 20,
  } = {}) {
    const query = buildQuery({
      status: statuses,
      year,
      search,
      page,
      pageSize,
    });
    return http.get(url(`${files.GET()}${query}`)).then(dataOf);
  },
  getFile(id) {
    return http.get(url(file.GET(id))).then(dataOf);
  },
  getAudit(id, page = 1, pageSize = 20) {
    return http
      .get(url(`${fileAudit.GET(id)}${buildQuery({ page, pageSize })}`))
      .then(dataOf);
  },
  createFile(data) {
    return http.post(url(files.GET()), data).then(dataOf);
  },
  updateFile(id, data, rowVersion) {
    return http.put(url(file.GET(id)), { ...data, rowVersion }).then(dataOf);
  },
  deleteFile(id) {
    return http.delete(url(file.GET(id))).then(() => undefined);
  },
  registerFile(id, rowVersion) {
    return http
      .post(url(fileAction.GET(id, "register")), { rowVersion })
      .then(dataOf);
  },
  rectifyFile(id, data, reason, rowVersion) {
    return http
      .post(url(fileAction.GET(id, "rectifications")), {
        ...data,
        reason,
        rowVersion,
      })
      .then(dataOf);
  },
  recordAssetsVerdict(id, hasAssetsOrDebts, rowVersion) {
    return http
      .post(url(fileAction.GET(id, "assets-verdict")), {
        hasAssetsOrDebts,
        rowVersion,
      })
      .then(dataOf);
  },
  classifyFile(id, rowVersion) {
    return http
      .post(url(fileAction.GET(id, "classify")), { rowVersion })
      .then(dataOf);
  },
  recordChamberRegistration(
    id,
    { notaryChamberId, chamberRegistrationNumber, chamberRegistrationDate },
    rowVersion
  ) {
    return http
      .post(url(fileAction.GET(id, "chamber-registration")), {
        notaryChamberId,
        chamberRegistrationNumber,
        chamberRegistrationDate,
        rowVersion,
      })
      .then(dataOf);
  },
  cancelFile(id, reason, rowVersion) {
    return http
      .post(url(fileAction.GET(id, "cancel")), { reason, rowVersion })
      .then(dataOf);
  },
  listDocuments(id) {
    return http.get(url(fileDocuments.GET(id))).then(dataOf);
  },
  // Sablonul cerut e mereu "SDPS"; API-ul refuza orice alt cod (vezi controller-ul).
  generateDocument(id, { notaryChamberId, rowVersion }) {
    return http
      .post(url(fileDocuments.GET(id)), {
        templateCode: "SDPS",
        notaryChamberId,
        rowVersion,
      })
      .then(dataOf);
  },
  // fileName vine de la apelant (elementul din lista sau documentul intors la generare); vezi
  // downloadBlob mai sus pentru motivul pentru care nu se citeste din antet in browser.
  downloadDocument(id, documentId, fileName) {
    return downloadBlob(
      fileDocument.GET(id, documentId, "download"),
      fileName,
      DEFAULT_DOCUMENT_NAME
    );
  },
  verifyDocument(id, documentId) {
    return http
      .get(url(fileDocument.GET(id, documentId, "verify")))
      .then(dataOf);
  },
  // C.D.P.S. necompletat: intai versiunea in vigoare, apoi fisierul ei. Doar 404-ul de la prima cerere
  // inseamna "sablonul nu e publicat" (DocumentTemplatesController.GetEffective raspunde NotFound() cand
  // nu exista nicio versiune in vigoare); un 404 la a doua cerere inseamna ca versiunea citita a disparut
  // intre timp (DownloadVersion), nu ca sablonul lipseste, deci nu primeste acelasi marcaj. Eroarea axios
  // trece mai departe neschimbata (cu response.status pastrat), doar cu proprietatea templateNotPublished
  // adaugata cand se aplica, ca pagina sa arate mesajul potrivit fara sa stie ea insasi care cerere a esuat.
  downloadBlankCdps() {
    return http
      .get(url(documentTemplate.GET("CDPS")))
      .catch((error) => {
        if (error && error.response && error.response.status === 404) {
          error.templateNotPublished = true;
        }
        throw error;
      })
      .then((response) => {
        const version = response.data;
        return downloadBlob(
          documentTemplateVersion.GET("CDPS", version.version),
          version.originalFileName,
          DEFAULT_CDPS_NAME
        );
      });
  },
  getRegister(year) {
    return http
      .get(url(`${register.GET()}${buildQuery({ year })}`))
      .then(dataOf);
  },
  findGeneralRegistryEntry(registryId, year, number) {
    return http
      .get(
        url(
          `${generalRegistryEntry.GET()}${buildQuery({
            registryId,
            year,
            number,
          })}`
        )
      )
      .then(dataOf);
  },
  listRegistries() {
    return http.get(url(registries.GET())).then(dataOf);
  },
  listNotaryChambers() {
    return http.get(url(notaryChambers.GET())).then(dataOf);
  },
};

export default SuccessionService;
